import { useEffect, useState } from "react";
import { MessageSquare, Send, Loader2, Clock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useT } from "@/lib/publicI18n";

export const fmtTime = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

// Public client notes on a shared mock-up: anyone with the link can leave a timestamped note; clicking a timecode seeks the player.
export default function ShareNotes({ token, pos, onSeek, onLoaded }) {
  const { t, locale, lang } = useT();
  const [notes, setNotes] = useState([]);
  const [author, setAuthor] = useState(() => localStorage.getItem("mf_share_author") || "");
  const [email, setEmail] = useState(() => localStorage.getItem("mf_share_email") || "");
  const [text, setText] = useState("");
  const [time, setTime] = useState(0);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${API}/share/${token}/comments`).then((r) => (r.ok ? r.json() : [])).then((j) => { setNotes(j); onLoaded?.(j); }).catch(() => {});
  }, [token]); // load once per link

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true); setError(null);
    try {
      const r = await fetch(`${API}/share/${token}/comments`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ author: author.trim() || t("guest"), time, text: text.trim(), email: email.trim() || null, lang }) });
      const j = await r.json();
      if (!r.ok) throw new Error(Array.isArray(j.detail) ? t("invalid_email") : j.detail || t("send_failed"));
      localStorage.setItem("mf_share_author", author.trim());
      localStorage.setItem("mf_share_email", email.trim());
      setText("");
      setNotes((n) => [...n, j].sort((a, b) => a.time - b.time));
      onLoaded?.([...notes, j]);
      if (j.notify) toast.success(t("note_sent_notify"));
    } catch (err) { setError(err.message); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-3" data-testid="share-notes">
      <p className="text-xs font-semibold flex items-center gap-2 text-cyan-200"><MessageSquare size={14} /> {t("notes_title")} <span className="text-muted-foreground font-normal">{t("notes_hint")}</span></p>
      <form onSubmit={send} className="rounded-xl border border-border bg-background/60 p-3 space-y-2" data-testid="share-note-form">
        <div className="flex gap-2 flex-wrap items-center">
          <input value={author} onChange={(e) => setAuthor(e.target.value)} maxLength={40} placeholder={t("your_name")} className="h-8 flex-1 min-w-[140px] rounded-md border border-border bg-transparent px-2 text-xs" data-testid="share-note-author" />
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("email_opt_reply")} className="h-8 flex-1 min-w-[200px] rounded-md border border-border bg-transparent px-2 text-xs" data-testid="share-note-email" />
          <button type="button" onClick={() => setTime(Math.round(pos * 10) / 10)} className="h-8 inline-flex items-center gap-1.5 rounded-md border border-cyan-500/40 px-2 text-xs font-mono text-cyan-300 hover:bg-cyan-500/10" title={t("capture_title")} data-testid="share-note-capture"><Clock size={12} /> {fmtTime(time)} <span className="text-muted-foreground">({t("capture", { t: fmtTime(pos) })})</span></button>
        </div>
        <textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={600} rows={2} placeholder={t("note_placeholder")} className="w-full rounded-md border border-border bg-transparent px-2 py-1.5 text-xs resize-none" data-testid="share-note-text" />
        <div className="flex items-center justify-between gap-2">
          {error ? <span className="text-[11px] text-red-300" data-testid="share-note-error">{error}</span> : <span className="text-[11px] text-muted-foreground">{text.length}/600</span>}
          <button type="submit" disabled={sending || !text.trim()} className="h-8 inline-flex items-center gap-1.5 rounded-md bg-cyan-500 px-3 text-xs font-semibold text-black disabled:opacity-50 hover:bg-cyan-400" data-testid="share-note-send">{sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} {t("send_note")}</button>
        </div>
      </form>
      {notes.length > 0 && (
        <ul className="space-y-1.5" data-testid="share-note-list">
          {notes.map((n) => (
            <li key={n.id} className={`rounded-lg border px-3 py-2 text-xs ${n.resolved ? "border-emerald-500/30 bg-emerald-500/5" : "border-border bg-card/60"}`} data-testid={`share-note-${n.id}`}>
              <div className="flex gap-2 items-start">
                <button type="button" onClick={() => onSeek?.(n.time)} className="font-mono text-cyan-300 hover:underline shrink-0" data-testid={`share-note-seek-${n.id}`}>{fmtTime(n.time)}</button>
                <div className="flex-1 min-w-0"><span className="font-semibold">{n.author}</span> <span className="text-muted-foreground">· {new Date(n.created_at).toLocaleDateString(locale)}</span><p className="mt-0.5 break-words">{n.text}</p></div>
                {n.resolved && <span className="inline-flex items-center gap-1 text-emerald-300 shrink-0" title={t("resolved_title")}><CheckCircle2 size={12} /> {t("resolved")}</span>}
              </div>
              {n.reply?.text && <p className="mt-1.5 ml-4 border-l-2 border-cyan-500/50 pl-2 italic text-cyan-100/90 break-words" data-testid={`share-note-reply-${n.id}`}><span className="not-italic font-semibold text-cyan-300">↳ {t("team")} · {n.reply.author}</span> · {n.reply.text}</p>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
